import { COUNTRIES_BY_CODE } from "@redotech/locale/countries";
import { CountryCode, isCountryCode } from "@redotech/locale/country-codes";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { lcgShippableCountries } from "@redotech/redo-conversion-core-common/redo-in-shipping/landed-cost-lane-countries";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { isCrossBorderForLandedCost } from "@redotech/redo-fulfillment-operations-common/shipping/util";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import * as css from "./checkout-rate-section.module.css";

function crossBorderDestinationCountry(
  fulfillmentGroup: FulfillmentGroupData,
): CountryCode | null {
  const destinationCountry = fulfillmentGroup.destinationAddress.countryCode;
  if (
    !destinationCountry ||
    !isCountryCode(destinationCountry) ||
    !isCrossBorderForLandedCost(
      fulfillmentGroup.originAddress.countryCode,
      destinationCountry,
    )
  ) {
    return null;
  }
  return destinationCountry;
}

export const LcgUnavailableCard = memo(function LcgUnavailableCard({
  fulfillmentGroup,
}: {
  fulfillmentGroup: FulfillmentGroupData | undefined;
}) {
  const team = useContext(TeamContext);
  const outboundLabelsClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const lcgDestinationCountry =
    !!team?.settings.landedCost?.enabled && fulfillmentGroup
      ? crossBorderDestinationCountry(fulfillmentGroup)
      : null;

  const enabledLanesLoad = useLoad(
    async (signal: AbortSignal) => {
      if (!lcgDestinationCountry) {
        return null;
      }
      const { options } =
        await outboundLabelsClient.getCarrierServiceCombinations(
          { includeDisabled: false, includeMerchantCarriers: true },
          { signal },
        );
      return options;
    },
    [outboundLabelsClient, lcgDestinationCountry],
  );

  if (
    !lcgDestinationCountry ||
    !enabledLanesLoad.value ||
    lcgShippableCountries(enabledLanesLoad.value).includes(
      lcgDestinationCountry,
    )
  ) {
    return null;
  }

  const countryName =
    COUNTRIES_BY_CODE[lcgDestinationCountry] ?? lcgDestinationCountry;
  return (
    <RedoBadge
      className={css.checkoutRateBadge}
      color="warning"
      text={
        <Text fontSize="xs" fontWeight="bold" whiteSpace="normal">
          {`Shipping DDU, DDP is unavailable to ${countryName} on your configured carriers`}
        </Text>
      }
    />
  );
});
